import { type ReactNode, type Ref, useEffect, useImperativeHandle, useState } from "react"

import { useTranslation } from "react-i18next"

import type { EngineSettings, SearchOptions, SearchSettings } from "@/api/filth-settings-api"

import { loadSearchSettings, saveSearchSettings } from "@/api/filth-settings-api"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { getApiErrorMessage } from "@/lib/api-error-message"

import {
  displaySearchValue,
  engineEntityValue,
  numericInputValue,
  parseSearchNumber,
  SEARCH_FIELDS,
  searchEnabledChoice,
  type SearchSettingsControls,
  validateSearchDraft,
} from "./filth-settings-utils"

export function FilthSearchSettings({
  id,
  library = false,
  embedded = false,
  onOverridesChange,
  controlsRef,
  onStateChange,
  engineSettings,
  candidateControls,
  disabled = false,
}: {
  id: string
  library?: boolean
  embedded?: boolean
  onOverridesChange?: (count: number) => void
  controlsRef?: Ref<SearchSettingsControls>
  onStateChange?: (state: { dirty: boolean; valid: boolean; loaded: boolean }) => void
  engineSettings?: EngineSettings | null
  candidateControls?: ReactNode
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const [settings, setSettings] = useState<SearchSettings | null>(null)
  const [draft, setDraft] = useState<SearchOptions>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const title = t(
    library
      ? "adminDashboard.filthSettings.searchOverrides"
      : "adminDashboard.filthSettings.searchDefaults"
  )

  useEffect(() => {
    let active = true
    setSettings(null)
    setError(null)
    setSaved(false)
    void loadSearchSettings(id, library)
      .then((data) => {
        if (active) {
          setSettings(data)
          setDraft(data.stored)
        }
      })
      .catch((err: unknown) => {
        if (active)
          setError(getApiErrorMessage(err, t("adminDashboard.filthSettings.searchLoadFailed")))
      })
    return () => {
      active = false
    }
  }, [id, library, t])

  const reload = async () => {
    setBusy(true)
    try {
      const data = await loadSearchSettings(id, library)
      setSettings(data)
      setDraft(data.stored)
      setError(null)
      setSaved(false)
    } catch (err) {
      setError(getApiErrorMessage(err, t("adminDashboard.filthSettings.searchReloadFailed")))
    } finally {
      setBusy(false)
    }
  }
  const save = async () => {
    if (!settings) return false
    setBusy(true)
    setSaved(false)
    setError(null)
    try {
      const data = await saveSearchSettings(
        id,
        { revision: settings.revision, options: draft },
        library
      )
      setSettings(data)
      setDraft(data.stored)
      setSaved(true)
      return true
    } catch (err) {
      setError(getApiErrorMessage(err, t("adminDashboard.filthSettings.saveFailed")))
      return false
    } finally {
      setBusy(false)
    }
  }
  const change = (group: string, field: string, value: boolean | number | null) => {
    setSaved(false)
    setDraft((current) => ({ ...current, [group]: { ...current[group], [field]: value } }))
  }
  const entityDisabled = engineSettings?.settings.some(
    (item) =>
      item.key === "serving.context_entity_arm" &&
      (item.kill_switch_active || item.value === "disabled")
  )
  const overrideCount = SEARCH_FIELDS.reduce(
    (count, { group, fields }) =>
      count +
      fields.filter(
        (field) =>
          draft[group]?.[field] != null &&
          draft[group][field] !==
            (group === "entity_arm" && engineSettings
              ? engineEntityValue(engineSettings, field)
              : settings?.inherited[group]?.[field])
      ).length,
    0
  )
  useEffect(() => {
    onOverridesChange?.(overrideCount)
  }, [overrideCount, onOverridesChange])
  const validation = settings ? validateSearchDraft(draft, settings, t) : null
  const dirty = SEARCH_FIELDS.some(({ group, fields }) =>
    fields.some(
      (field) => (draft[group]?.[field] ?? null) !== (settings?.stored[group]?.[field] ?? null)
    )
  )
  useEffect(() => {
    onStateChange?.({ dirty, valid: !!settings && !validation && !busy, loaded: !!settings })
  }, [dirty, settings, validation, busy, onStateChange])
  useImperativeHandle(controlsRef, () => ({ save, reload }))
  return (
    <section
      aria-label={title}
      className={embedded ? "min-w-0 space-y-6" : "bg-card min-w-0 space-y-6 rounded-lg border p-6"}
    >
      {!embedded && <h4 className="font-display">{title}</h4>}
      {!embedded && (
        <p className="text-sm">
          {t("adminDashboard.filthSettings.overrideSummary", { count: overrideCount })}
        </p>
      )}
      <p className="text-muted-foreground max-w-prose text-sm leading-relaxed">
        {t("adminDashboard.filthSettings.searchDescription", {
          defaultSource: t(
            library
              ? "adminDashboard.filthSettings.instanceDefault"
              : "adminDashboard.filthSettings.processDefault"
          ),
        })}
      </p>
      {!settings && !error && (
        <p className="text-muted-foreground rounded-md border p-4 text-sm" role="status">
          {t("adminDashboard.filthSettings.loadingSearch")}
        </p>
      )}
      {settings &&
        SEARCH_FIELDS.map(({ group, fields }) => (
          <details key={group} open className="min-w-0 space-y-4 border-t pt-4">
            <summary className="cursor-pointer">
              <h4 className="font-display inline text-base">
                {t(`adminDashboard.filthSettings.groups.${group}`)}
              </h4>
            </summary>
            <fieldset className="min-w-0 space-y-4" disabled={busy || disabled}>
              <legend className="sr-only">
                {t(`adminDashboard.filthSettings.groups.${group}`)}
              </legend>
              <p className="text-muted-foreground max-w-prose text-sm leading-relaxed">
                {t(`adminDashboard.filthSettings.groupDescriptions.${group}`)}
              </p>
              {fields.map((field) => {
                const fieldId = `search-${library ? "library" : "instance"}-${id}-${group}-${field}`
                const value = draft[group]?.[field] ?? null
                const stored = settings.stored[group]?.[field]
                const inherited =
                  group === "entity_arm" && engineSettings
                    ? engineEntityValue(engineSettings, field)
                    : settings.inherited[group]?.[field]
                const savedEffective =
                  group === "entity_arm" && engineSettings
                    ? (stored ?? inherited)
                    : (settings.effective[group]?.[field] ?? inherited)
                let effective = value === (stored ?? null) ? savedEffective : (value ?? inherited)
                if (group === "entity_arm" && entityDisabled)
                  effective = field === "enabled" ? false : 0
                const ownSource = library ? "library" : "instance"
                const source = value == null ? settings.inherited_sources[group][field] : ownSource
                const bounds = settings.bounds[group]?.[field]
                let inheritLabel = library
                  ? "adminDashboard.filthSettings.useInstanceInherit"
                  : "adminDashboard.filthSettings.useDefaultInherit"
                if (group === "entity_arm" && engineSettings)
                  inheritLabel = "adminDashboard.filthSettings.useFilthSetting"
                return (
                  <div
                    key={field}
                    className="grid min-w-0 items-start gap-4 border-t pt-4 sm:grid-cols-[minmax(0,1fr)_20rem]"
                  >
                    <div className="min-w-0 space-y-2">
                      <Label className="text-sm" htmlFor={fieldId}>
                        {t(`adminDashboard.filthSettings.fields.${field}`)}
                      </Label>
                      <p
                        className="text-muted-foreground max-w-prose text-sm leading-relaxed"
                        id={`${fieldId}-help`}
                      >
                        {t(`adminDashboard.filthSettings.fieldDescriptions.${group}.${field}`)}
                      </p>
                      {value != null && value !== inherited && (
                        <Badge variant="outline">
                          {t("adminDashboard.filthSettings.overridden")}
                        </Badge>
                      )}
                    </div>
                    {field === "enabled" ? (
                      <Select
                        value={searchEnabledChoice(value)}
                        onValueChange={(next) =>
                          change(group, field, next === "inherit" ? null : next === "on")
                        }
                      >
                        <SelectTrigger
                          aria-describedby={`${fieldId}-help`}
                          className="min-h-10 w-full"
                          id={fieldId}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inherit">
                            {t(inheritLabel)}
                            {" · "}
                            {typeof inherited === "boolean"
                              ? t(`adminDashboard.filthSettings.${inherited ? "on" : "off"}`)
                              : t("adminDashboard.filthSettings.filthDefault")}
                          </SelectItem>
                          <SelectItem value="on">{t("adminDashboard.filthSettings.on")}</SelectItem>
                          <SelectItem value="off">
                            {t("adminDashboard.filthSettings.off")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex flex-wrap items-center gap-3">
                        <Input
                          aria-describedby={`${fieldId}-help`}
                          className="min-h-10 w-32 flex-1"
                          id={fieldId}
                          max={bounds?.[1]}
                          min={bounds?.[0]}
                          step={bounds?.[2] ? 1 : "any"}
                          type="number"
                          placeholder={
                            group === "entity_arm" && engineSettings
                              ? t("adminDashboard.filthSettings.filthValue", {
                                  value: inherited ?? t("adminDashboard.filthSettings.unavailable"),
                                })
                              : t("adminDashboard.filthSettings.inherit")
                          }
                          value={numericInputValue(
                            group === "entity_arm" && engineSettings
                              ? value
                              : (value ?? effective ?? null)
                          )}
                          onChange={(event) =>
                            change(
                              group,
                              field,
                              parseSearchNumber(event.target.value, event.target.validity.badInput)
                            )
                          }
                        />
                        <Button
                          size="default"
                          type="button"
                          variant="outline"
                          onClick={() => change(group, field, null)}
                        >
                          {t("adminDashboard.filthSettings.resetDefault")}
                        </Button>
                      </div>
                    )}
                    {field === "enabled" && value != null && (
                      <Button
                        className="sm:col-start-2"
                        type="button"
                        variant="outline"
                        onClick={() => change(group, field, null)}
                      >
                        {t("adminDashboard.filthSettings.resetDefault")}
                      </Button>
                    )}
                    <details className="text-muted-foreground text-sm sm:col-span-2">
                      <summary className="cursor-pointer">
                        {t("adminDashboard.filthSettings.details")}
                      </summary>
                      <p className="max-w-prose pt-3 leading-relaxed">
                        {t("adminDashboard.filthSettings.searchValues", {
                          stored: displaySearchValue(
                            stored,
                            t("adminDashboard.filthSettings.inheritValue")
                          ),
                          inherited: displaySearchValue(
                            inherited,
                            t("adminDashboard.filthSettings.filthDefault")
                          ),
                          effective: displaySearchValue(
                            effective,
                            t("adminDashboard.filthSettings.filthDefault")
                          ),
                          source: source
                            ? t(`adminDashboard.filthSettings.sources.${source}`)
                            : t("adminDashboard.filthSettings.filthDefault"),
                        })}
                        {bounds
                          ? t("adminDashboard.filthSettings.bounds", {
                              min: bounds[0],
                              max: bounds[1],
                              integer: bounds[2]
                                ? t("adminDashboard.filthSettings.integerSuffix")
                                : "",
                            })
                          : ""}
                      </p>
                    </details>
                  </div>
                )
              })}
              {group === "entity_arm" && entityDisabled && (
                <p className="text-muted-foreground text-sm" role="status">
                  {t("adminDashboard.filthSettings.entityKillSwitch")}
                </p>
              )}
              {group === "entity_arm" && candidateControls}
            </fieldset>
          </details>
        ))}
      {(error !== null || validation !== null) && (
        <p
          className="bg-destructive text-destructive-foreground border-foreground rounded-md border p-4 text-sm leading-relaxed"
          role="alert"
        >
          {error || validation}
        </p>
      )}
      {saved && (
        <p className="rounded-md border p-4 text-sm leading-relaxed" role="status">
          {t("adminDashboard.filthSettings.searchSaved")}
        </p>
      )}
      {!embedded && (
        <div className="flex flex-wrap gap-3 border-t pt-6">
          <Button
            disabled={busy || !settings || !!validation}
            type="button"
            onClick={() => void save()}
          >
            {t(
              library
                ? "adminDashboard.filthSettings.saveOverrides"
                : "adminDashboard.filthSettings.saveDefaults"
            )}
          </Button>
          <Button
            disabled={busy || disabled}
            type="button"
            variant="outline"
            onClick={() => void reload()}
          >
            {t("adminDashboard.filthSettings.reload")}
          </Button>
        </div>
      )}
    </section>
  )
}
